import { useEffect, useState, ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import clsx from "clsx";
import { Button } from "../ui/button";
import { LucideLoader2 } from "lucide-react";

type AnimatedButtonProps = {
  children?: ReactNode;
  onClick: () => void;
  loading?: boolean;
  active?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
};

const AnimatedButton = ({
  children,
  onClick,
  loading = false,
  active = false,
  disabled = false,
  className,
  id,
}: AnimatedButtonProps) => {
  const [animateActive, setAnimateActive] = useState(false);

  useEffect(() => {
    if (active) {
      setAnimateActive(true);
      setTimeout(() => setAnimateActive(false), 700);
    }
  }, [active]);

  return (
    <Button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        "relative inline-flex items-center w-[10rem] justify-center px-4 py-2.5 rounded-lg overflow-hidden",
        "font-medium text-sm transition-all duration-300 ease-out",
        "border border-gray-200 dark:border-gray-700",
        "shadow-sm hover:shadow-md",
        // Default state
        !active &&
          !disabled && [
            "bg-white dark:bg-gray-800",
            "text-gray-700 dark:text-gray-300",
            "hover:bg-gray-50 dark:hover:bg-gray-750",
            "hover:border-gray-300 dark:hover:border-gray-600",
          ],
        // Disabled state
        disabled && [
          "bg-gray-100 dark:bg-gray-800",
          "text-gray-400 dark:text-gray-500",
          "border-gray-200 dark:border-gray-700",
          "cursor-not-allowed opacity-50",
        ],
        // Active state
        active &&
          !disabled && [
            "bg-[--accent-primary]",
            "border-blue-500",
            "text-white",
            "hover:bg-[--accent-primary]",
          ],
        className,
      )}
    >
      {/* Continuous shimmer effect for active state */}

      {active && (
        <motion.div
          key="shimmer"
          className="absolute inset-0"
          initial={{ x: "-100%" }}
          animate={{ x: "100%" }}
          exit={{ x: "100%" }}
          transition={{
            duration: 1.5,
            ease: "easeInOut",
            repeat: Infinity,
            repeatDelay: 0.5,
          }}
        >
          <div className="h-full w-10 bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-12" />
        </motion.div>
      )}

      {/* Content */}
      <span className="relative z-10 flex items-center gap-2">
        {loading && <LucideLoader2 className=" animate-spin w-full" />}

        {/* Label */}
        <motion.span
          className="font-bold"
          animate={{
            scale: active && !disabled ? [1, 1.02, 1] : 1,
          }}
          transition={{ duration: 0.3 }}
        >
          {children}
        </motion.span>
      </span>

      {/* Success ripple effect */}
      <AnimatePresence>
        {animateActive && (
          <motion.div
            key="ripple"
            className="absolute inset-0 border-2 border-green-400 rounded-lg"
            initial={{ scale: 1, opacity: 0.8 }}
            animate={{ scale: 1.1, opacity: 0 }}
            exit={{ scale: 1.2, opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          />
        )}
      </AnimatePresence>
    </Button>
  );
};

export default AnimatedButton;
